import { OrderDirection } from "_shared/_api/ordering/order-direction";
import PaginationParameters from "_shared/_api/queries/pagination/pagination-parameters";
import { useAttorneyHubDispatch } from "_shared/_stores/redux-store";
import AHDate from "_shared/dates/date";
import Guid from "_shared/guids/guid";
import { MRT_ColumnFiltersState as MRTColumnFiltersState } from "material-react-table";
import PermissionfilterParameters from "permissions/_api/queries/filtering/permission-filter-parameters";
import { PermissionfilterParametersProps } from "permissions/_api/queries/filtering/permission-filter-schema";
import PermissionorderParameters, {
  PermissionOrderField,
} from "permissions/_api/queries/ordering/permission-order-params";
import PermissionsQuery, { PermissionView } from "permissions/_api/queries/permission-query";
import PermissionTemplateQuery from "permissions/_api/request-contracts/permission-template-query";
import {
  getPermissionTemplatesQueryByInterface,
  updatePermissionTemplateInterfaceQuery,
} from "permissions/_stores/permission-templates-redux-slice";
import {
  getPermissionsQueryByInterface,
  updatePermissionInterfaceQuery
} from "permissions/_stores/permissions-redux-slice";
import { ColumnFilter } from "permissions/_views/types/permission-types";
import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useLoaderData, useLocation, useNavigate } from "react-router-dom";
import { useSession } from "users/__session/session-context";

// Local types
export type UsePermissionsQuerySyncState = {
  columnFilters: MRTColumnFiltersState;
  setColumnFilters: React.Dispatch<React.SetStateAction<MRTColumnFiltersState>>;
  sorting: Array<{ id: string; desc: boolean }>;
  setSorting: React.Dispatch<React.SetStateAction<Array<{ id: string; desc: boolean }>>>;
  pagination: { pageIndex: number; pageSize: number };
  setPagination: React.Dispatch<React.SetStateAction<{ pageIndex: number; pageSize: number }>>;
  permissionsQuery: PermissionsQuery;
  permissionTemplatesQuery: PermissionTemplateQuery;
};

/**
 * Centralizes URL <-> Redux query synchronization for the Permissions table.
 *
 * Responsibilities:
 * 1) Read URLSearchParams and dispatch updatePermissionInterfaceQuery with a PermissionsQuery
 * 2) Reflect column filter changes back into the URL
 * 3) Own table view state (sorting/pagination/columnFilters)
 */
export default function usePermissionsQuerySync(): UsePermissionsQuerySyncState {
  // Router & session
  const routeParams = useLoaderData();
  const location = useLocation();
  const navigate = useNavigate();
  const session = useSession();

  // Redux
  const dispatch = useAttorneyHubDispatch();
  const interfaceName = "permissionsTable";
  const templatesInterfaceName = "permissionTemplatesTable";
  const permissionsQuery = useSelector(getPermissionsQueryByInterface(interfaceName));
  const permissionTemplatesQuery = useSelector(getPermissionTemplatesQueryByInterface(templatesInterfaceName));

  // Table state
  const initialColumnFilters = useMemo(() => buildColumnFilters(location.search), [location.search]);

  const [columnFilters, setColumnFilters] = useState<MRTColumnFiltersState>(initialColumnFilters);
  const [sorting, setSorting] = useState<Array<{ id: string; desc: boolean }>>([{ id: "grantee", desc: true }]);
  const [pagination, setPagination] = useState<{ pageIndex: number; pageSize: number }>({
    pageIndex: 0,
    pageSize: 10,
  });

  if (!permissionsQuery || !permissionTemplatesQuery) {
    const defaultPermissionsQuery = buildPermissionsQuery();

    if (!permissionsQuery) {
      dispatch(
        updatePermissionInterfaceQuery({
          interfaceName,
          query: defaultPermissionsQuery,
        })
      );
    }

    if (!permissionTemplatesQuery) {
      const defaultTemplatesQuery = new PermissionTemplateQuery({
        filterParameters: defaultPermissionsQuery.filterParameters,
        orderParameters: defaultPermissionsQuery.orderParameters,
        paginationParameters: defaultPermissionsQuery.paginationParameters,
      });

      dispatch(
        updatePermissionTemplateInterfaceQuery({
          interfaceName: templatesInterfaceName,
          query: defaultTemplatesQuery,
        })
      );
    }
  }

  function buildColumnFilters(search: string): MRTColumnFiltersState {
    const params = new URLSearchParams(search);
    const filters: MRTColumnFiltersState = [];

    if (params.has("granteeUserId")) {
      filters.push({ id: "grantee", value: params.get("granteeUserId")! });
    }
    if (params.has("grantorUserId")) {
      filters.push({ id: "grantor", value: params.get("grantorUserId")! });
    }
    if (params.has("resource")) {
      filters.push({ id: "resource", value: params.get("resource")! });
    }
    if (params.has("name")) {
      filters.push({ id: "name", value: params.get("name")! });
    }

    const minCreatedDateString = params.get("minCreatedDate");
    const maxCreatedDateString = params.get("maxCreatedDate");
    if (minCreatedDateString || maxCreatedDateString) {
      filters.push({
        id: "created",
        value: [AHDate.fromJSON(minCreatedDateString)?.value, AHDate.fromJSON(maxCreatedDateString)?.value],
      });
    }

    const minDisabledDateString = params.get("minDisabledDate");
    const maxDisabledDateString = params.get("maxDisabledDate");
    if (minDisabledDateString || maxDisabledDateString) {
      filters.push({
        id: "disabled",
        value: [AHDate.fromJSON(minDisabledDateString)?.value, AHDate.fromJSON(maxDisabledDateString)?.value],
      });
    }

    return filters;
  }

  // Helper to build filter parameters from URLSearchParams
  function buildFilterParameters(query: URLSearchParams): PermissionfilterParameters {
    // Start with default values
    const filters: PermissionfilterParametersProps = {
      showDisabled: true,
    };

    // ---- Grantee (single) ----
    const granteeUserIds: Guid[] = [];
    const granteeUserIdString = query.get("granteeUserId");
    const granteeUserId = granteeUserIdString ? new Guid(granteeUserIdString) : undefined;
    if (granteeUserId && !granteeUserId.isEqualTo(Guid.EMPTY)) {
      granteeUserIds.push(granteeUserId);
    }

    // ---- Grantee (multi) ----
    const multiGranteeIds = query
      .getAll("granteeUserIds")
      .map((c) => c.trim())
      .filter(Boolean)
      .map((c) => new Guid(c));

    if (multiGranteeIds.length) {
      granteeUserIds.push(...multiGranteeIds);
    }

    if (granteeUserIds.length) {
      filters.granteeUserIds = granteeUserIds;
    }

    // ---- Grantor ----
    const grantorUserIdString = query.get("grantorUserId");
    const grantorUserId = grantorUserIdString ? new Guid(grantorUserIdString, { allowEmpty: true }) : undefined;

    if (grantorUserId && !grantorUserId.isEqualTo(Guid.EMPTY)) {
      filters.grantorUserId = grantorUserId;
    }

    // ---- Name ----
    const nameString = query.get("name");
    if (nameString) {
      filters.name = nameString;
    }

    // ---- Resource ----
    const resourceString = query.get("resource");
    if (resourceString) {
      // PermissionfilterParameters.resource is `unknown`, we just store the raw string;
      // it will be stringified appropriately in searchParams.
      filters.resource = resourceString;
    }

    // ---- Created date range ----
    const minCreatedDateString = query.get("minCreatedDate");
    const maxCreatedDateString = query.get("maxCreatedDate");

    const minCreatedDate = minCreatedDateString ? AHDate.fromJSON(minCreatedDateString) ?? undefined : undefined;
    const maxCreatedDate = maxCreatedDateString ? AHDate.fromJSON(maxCreatedDateString) ?? undefined : undefined;

    if (minCreatedDate) {
      filters.minCreatedDate = minCreatedDate;
    }
    if (maxCreatedDate) {
      filters.maxCreatedDate = maxCreatedDate;
    }

    // ---- Disabled date range ----
    const minDisabledDateString = query.get("minDisabledDate");
    const maxDisabledDateString = query.get("maxDisabledDate");

    const minDisabledDate = minDisabledDateString ? AHDate.fromJSON(minDisabledDateString) ?? undefined : undefined;
    const maxDisabledDate = maxDisabledDateString ? AHDate.fromJSON(maxDisabledDateString) ?? undefined : undefined;

    if (minDisabledDate) {
      filters.minDisabledDate = minDisabledDate;
    }
    if (maxDisabledDate) {
      filters.maxDisabledDate = maxDisabledDate;
    }

    // ---- Has parent flag ----
    const hasParentString = query.get("hasParent");
    if (hasParentString !== null) {
      filters.hasParent = hasParentString === "true";
    }

    // ---- Categories (multi) ----
    const urlCategories = query
      .getAll("categories")
      .map((c) => c.trim())
      .filter(Boolean);
    if (urlCategories.length) {
      filters.categories = urlCategories;
    }

    // ---- Access Type (enum as string) ----
    const urlAccessType = query.get("accessType");
    if (urlAccessType) {
      filters.accessType = urlAccessType.trim();
    }

    // Finally construct the immutable filter params object
    return new PermissionfilterParameters(filters);
  }

  // Helper to build order parameters from sorting
  function buildOrderParameters(sorting: Array<{ id: string; desc: boolean }>): PermissionorderParameters {
    let orderBy: PermissionOrderField | undefined;
    if (sorting.length > 0 && sorting[0].id === "created") {
      orderBy = PermissionOrderField.CreatedDate;
    }
    if (sorting.length > 0 && sorting[0].id === "resource") {
      orderBy = PermissionOrderField.Resource;
    }
    if (sorting.length > 0 && sorting[0].id === "disabled") {
      orderBy = PermissionOrderField.DisabledDate;
    }

    return new PermissionorderParameters({
      orderBy: orderBy,
      orderDir: sorting.length > 0 && !sorting[0].desc ? OrderDirection.Ascending : OrderDirection.Descending,
    });
  }

  function buildPermissionsQuery(): PermissionsQuery {
    const query = new URLSearchParams(location.search);
    const filterParameters = buildFilterParameters(query);
    const orderParameters = buildOrderParameters(sorting);
    const paginationParameters = PaginationParameters.create({
      pageIndex: pagination.pageIndex,
      pageSize: pagination.pageSize,
    });

    return new PermissionsQuery({
      filterParameters,
      orderParameters,
      paginationParameters,
      permissionView: PermissionView.Grantee,
      previewSize: 3,
    });
  }

  // Build Redux permissions query from URL + view state
  useEffect(() => {
    if (!session.user?.legalEntityId) return;

    const newQuery = buildPermissionsQuery();
    if (newQuery.isEqualTo(permissionsQuery)) return;

    dispatch(
      updatePermissionInterfaceQuery({
        interfaceName,
        query: newQuery,
      })
    );

    const newTemplatesQuery = new PermissionTemplateQuery({
      filterParameters: newQuery.filterParameters,
      orderParameters: newQuery.orderParameters,
      paginationParameters: newQuery.paginationParameters,
    });

    if (newTemplatesQuery.isEqualTo(permissionTemplatesQuery)) return;

    dispatch(
      updatePermissionTemplateInterfaceQuery({
        interfaceName: templatesInterfaceName,
        query: newTemplatesQuery,
      })
    );
  }, [routeParams, location, pagination.pageIndex, pagination.pageSize, sorting, session?.user?.legalEntityId]);

  // Reflect column filter changes -> URLSearchParams
  useEffect(() => {
    const prev = new URLSearchParams(location.search);
    const next = new URLSearchParams(location.search);

    const setKeyIfFilter = (
      filter: ColumnFilter | undefined,
      key: string,
      mapValue: (v: unknown) => string | null = (v) => {
        if (v == null) return null;
        if (v instanceof Date) return v.toISOString();
        if (typeof v === "object") return JSON.stringify(v);
        if (typeof v === "string" || typeof v === "number" || typeof v === "boolean") return String(v);
        return JSON.stringify(v);
      }
    ) => {
      if (!filter) return;
      const mapped = mapValue(filter.value);
      if (mapped === null || mapped === "") next.delete(key);
      else next.set(key, mapped);
    };

    // Grantee
    setKeyIfFilter(
      columnFilters.find((f) => f.id === "grantee"),
      "granteeUserId"
    );

    // Grantor
    setKeyIfFilter(
      columnFilters.find((f) => f.id === "grantor"),
      "grantorUserId"
    );

    // Resource
    setKeyIfFilter(
      columnFilters.find((f) => f.id === "resource"),
      "resource"
    );

    // Created range
    const createdFilter: ColumnFilter | undefined = columnFilters.find((f) => f.id === "created");
    if (createdFilter) {
      const createdValue = createdFilter.value as [AHDate | undefined, AHDate | undefined] | undefined;
      const minCreatedDate: AHDate | undefined = createdValue?.[0];
      const maxCreatedDate: AHDate | undefined = createdValue?.[1];
      if (minCreatedDate) next.set("minCreatedDate", minCreatedDate.toISOString());
      else next.delete("minCreatedDate");
      if (maxCreatedDate) next.set("maxCreatedDate", maxCreatedDate.toISOString());
      else next.delete("maxCreatedDate");
    }

    // Disabled range
    const disabledFilter: ColumnFilter | undefined = columnFilters.find((f) => f.id === "disabled");
    if (disabledFilter) {
      const disabledValue = disabledFilter.value as [AHDate | undefined, AHDate | undefined] | undefined;
      const minDisabledDate: AHDate | undefined = disabledValue?.[0];
      const maxDisabledDate: AHDate | undefined = disabledValue?.[1];
      if (minDisabledDate) next.set("minDisabledDate", minDisabledDate.toISOString());
      else next.delete("minDisabledDate");
      if (maxDisabledDate) next.set("maxDisabledDate", maxDisabledDate.toISOString());
      else next.delete("maxDisabledDate");
    }

    const prevStr = prev.toString();
    const nextStr = next.toString();
    if (prevStr !== nextStr) navigate({ search: nextStr });
  }, [columnFilters, navigate]);


  // Reflect URLSearchParams changes -> column filters
  useEffect(() => {
    const prevColumnFilters = columnFilters;
    const newColumnFilters = buildColumnFilters(location.search);

    // Check for changes
    let changed = false;
    if (prevColumnFilters.length === newColumnFilters.length) {
      for (const prevFilter of prevColumnFilters) {
        const newFilter = newColumnFilters.find((f) => f.id === prevFilter.id);
        if (!newFilter || newFilter.value !== prevFilter.value) {
          changed = true;
          break;
        }
      }
    }
    else {
      changed = true;
    }

    if (changed) {
      setColumnFilters(newColumnFilters);
    }
  }, [location.search]);

  return {
    columnFilters,
    setColumnFilters,
    sorting,
    setSorting,
    pagination,
    setPagination,
    permissionsQuery: permissionsQuery!,
    permissionTemplatesQuery: permissionTemplatesQuery!,
  };
}
